import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi, getErrorMessage } from '../services/api';
import { cn } from '../lib/utils';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import { FieldStatusList } from '../components/candidate/FieldStatusList';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import DOMPurify from 'dompurify';
import { 
  CheckCircle2, 
  AlertCircle, 
  Calendar as CalendarIcon,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  Lock,
  ZoomIn,
  ZoomOut,
  Upload,
  FileText,
  Trash2,
  PenTool
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import SignaturePad from 'signature_pad';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;


/**
 * Searchable dropdown for picklist fields with many options.
 * Shows a search input at the top of the dropdown panel.
 */
function SearchableDropdown({ fieldId, fieldStyle, baseClasses, scale, value, displayValue, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;
  return (
    <div key={fieldId} style={fieldStyle} className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`${baseClasses} w-full h-full text-left text-xs rounded border-2 px-2 flex items-center justify-between ${
              value ? 'bg-green-50 border-green-500' : 'bg-white border-blue-400'
            }`}
            style={{ fontSize: `${Math.max(10, 12 * scale)}px` }}
            data-testid={`field-${fieldId}`}
          >
            <span className="truncate">{displayValue || <span className="text-muted-foreground">Select…</span>}</span>
            <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-72 max-h-80 overflow-hidden z-[100]" align="start">
          <div className="p-2 border-b sticky top-0 bg-white z-10">
            <input
              type="text"
              className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              data-testid={`field-${fieldId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center">No matches</p>
            ) : (
              filtered.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2 ${isSelected ? 'bg-green-50 font-medium' : ''}`}
                    onClick={() => { onChange(String(opt.value)); setOpen(false); setSearch(''); }}
                    data-testid={`field-${fieldId}-option-${opt.value}`}
                  >
                    {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                    <span className="flex-1">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}


/**
 * Multi-select search panel — used inside the checkbox popover.
 * Keeps search state in React for proper filtering.
 */
function MultiSelectSearchPanel({ fieldId, options, selectedIds, toggleId, onClear }) {
  const [search, setSearch] = useState('');
  const showSearch = options.length > 8;
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;
  return (
    <>
      <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
        <span className="text-xs font-medium" data-testid={`field-${fieldId}-selected-count`}>
          {selectedIds.length} selected
        </span>
        {selectedIds.length > 0 && (
          <button type="button" onClick={onClear} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`field-${fieldId}-clear`}>
            Clear
          </button>
        )}
      </div>
      {showSearch && (
        <div className="p-2 border-b bg-white">
          <input
            type="text"
            className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            data-testid={`field-${fieldId}-search`}
          />
        </div>
      )}
      <div className="max-h-56 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground text-center">No matches</p>
        ) : (
          filtered.map((opt) => {
            const idStr = String(opt.value);
            const checked = selectedIds.includes(idStr);
            return (
              <label key={idStr} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer text-sm" data-testid={`field-${fieldId}-option-${idStr}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleId(idStr)} />
                <span className="flex-1">{opt.label}</span>
              </label>
            );
          })
        )}
      </div>
    </>
  );
}


/**
 * Attachment-only template panel — replaces the PDF viewer when the
 * recruiter has flagged a template as `template_mode: "attachment_only"`.
 *
 * The candidate uploads files for each checklist item, reviews the
 * uploaded list, then signs an acknowledgement. Submission still flows
 * through the standard `/complete/{token}/submit` endpoint, so audit
 * certificate generation and Bullhorn write-back work unchanged.
 */
function AttachmentOnlyPanel({
  token,
  checklist,
  signatureValue,
  onOpenSignaturePad,
  onSubmit,
  submitting,
  hasSignature,
}) {
  const [attachmentsByItem, setAttachmentsByItem] = useState({});
  const [uploadingItemId, setUploadingItemId] = useState(null);

  const reload = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/complete/${token}/attachments`);
      const map = {};
      for (const a of res.data?.attachments || []) {
        const key = a.checklist_item_id || a.field_id || '__unsorted__';
        if (!map[key]) map[key] = [];
        map[key].push(a);
      }
      setAttachmentsByItem(map);
    } catch (e) {
      // empty list is a valid initial state
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const ALLOWED = '.jpg,.jpeg,.png,.heic,.heif,.pdf';

  const handleUpload = async (item, file) => {
    if (!file) return;
    setUploadingItemId(item.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('field_id', `attachment_only:${item.id}`);
      fd.append('field_label', item.name || 'Attachment');
      fd.append('checklist_item_id', item.id);
      await axios.post(`${API_URL}/api/complete/${token}/upload-attachment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${file.name}`);
      await reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploadingItemId(null);
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await axios.delete(`${API_URL}/api/complete/${token}/attachments/${attachmentId}`);
      await reload();
    } catch {
      toast.error('Failed to remove file');
    }
  };

  const allRequiredUploaded = checklist
    .filter((c) => c.required)
    .every((c) => (attachmentsByItem[c.id] || []).length > 0);

  return (
    <div className="bg-white border border-slate-300 rounded-xl shadow-lg p-5 sm:p-6 space-y-5" data-testid="attachment-only-panel">
      <div>
        <h2 className="text-lg font-bold mb-1">Documents to upload</h2>
        <p className="text-xs text-muted-foreground">
          Accepted file types: PDF, JPG, PNG, HEIC. Maximum 10MB per file.
        </p>
      </div>

      {checklist.length === 0 ? (
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
          The recruiter hasn't specified which documents to upload. Please contact them for guidance.
        </div>
      ) : (
        <div className="space-y-3">
          {checklist.map((item) => {
            const uploaded = attachmentsByItem[item.id] || [];
            const isComplete = uploaded.length > 0;
            const isUploading = uploadingItemId === item.id;
            return (
              <div
                key={item.id}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  isComplete ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white'
                }`}
                data-testid={`checklist-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    {isComplete ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.required ? <span className="text-red-500 font-medium">Required</span> : 'Optional'}
                        {uploaded.length > 0 && ` · ${uploaded.length} file${uploaded.length === 1 ? '' : 's'} uploaded`}
                      </p>
                    </div>
                  </div>
                  <label
                    className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                    data-testid={`upload-button-${item.id}`}
                  >
                    {isUploading ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {isUploading ? 'Uploading…' : 'Add file'}
                    <input
                      type="file"
                      className="hidden"
                      accept={ALLOWED}
                      disabled={isUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) handleUpload(item, f);
                      }}
                    />
                  </label>
                </div>
                {uploaded.length > 0 && (
                  <ul className="space-y-1 mt-2">
                    {uploaded.map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-white border border-emerald-200 text-xs"
                      >
                        <span className="truncate">{att.filename}</span>
                        <button
                          type="button"
                          onClick={() => handleDelete(att.id)}
                          className="text-red-500 hover:text-red-700 font-medium"
                          data-testid={`remove-attachment-${att.id}`}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Acknowledgement signature */}
      <div className="border-t pt-5">
        <h3 className="font-medium text-sm mb-2">Sign to confirm</h3>
        <p className="text-xs text-muted-foreground mb-3">
          By signing below you confirm the uploaded documents are authentic and belong to you.
        </p>
        {hasSignature ? (
          <div
            className="p-3 rounded-lg border border-emerald-300 bg-emerald-50/50 flex items-center gap-3 cursor-pointer"
            onClick={onOpenSignaturePad}
            data-testid="acknowledgement-signature-preview"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-900">Acknowledgement signed</p>
              <p className="text-xs text-emerald-700">Click to re-sign</p>
            </div>
            {typeof signatureValue === 'string' && signatureValue.startsWith('data:image') && (
              <img src={signatureValue} alt="Your signature" className="h-10 border border-emerald-200 rounded bg-white" />
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-center border-dashed border-2 border-slate-300 h-14"
            onClick={onOpenSignaturePad}
            data-testid="open-acknowledgement-signature-button"
          >
            <PenTool className="w-4 h-4 mr-2" /> Click to sign acknowledgement
          </Button>
        )}
      </div>

      <div className="border-t pt-4">
        <Button
          onClick={onSubmit}
          disabled={submitting || !allRequiredUploaded || !hasSignature}
          className={`w-full h-11 ${(allRequiredUploaded && hasSignature) ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-300'}`}
          data-testid="submit-attachment-only-button"
        >
          {submitting ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Submitting…</>
          ) : !allRequiredUploaded ? (
            'Upload all required documents to continue'
          ) : !hasSignature ? (
            'Sign acknowledgement to continue'
          ) : (
            <><CheckCircle2 className="w-4 h-4 mr-2" />Submit</>
          )}
        </Button>
      </div>
    </div>
  );
}


export default function CandidateCompletionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const autoSaveTimer = useRef(null);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState(null);
  const [template, setTemplate] = useState(null);
  // Memoize the template PDF URL so cache-busting timestamp doesn't change on every render
  const templatePdfUrl = useMemo(() => {
    if (!template?.file_url) return null;
    return `${API_URL}${template.file_url}${template.file_url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  }, [template?.file_url]);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [prefilledValues, setPrefilledValues] = useState({});
  const [portalToken, setPortalToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [scale, setScale] = useState(1);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [signatureFieldId, setSignatureFieldId] = useState(null);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pdfWidth, setPdfWidth] = useState(612); // Default PDF width (US Letter)
  const [showFieldsList, setShowFieldsList] = useState(false);
  const [attachments, setAttachments] = useState({});  // { field_id: [ {id, filename, size, content_type}, ... ] }
  const [uploadingFieldId, setUploadingFieldId] = useState(null);
  // Cache of object URLs for inline image/pdf previews, keyed by attachment id.
  // The download endpoint serves bytes with Content-Disposition: attachment,
  // so we fetch as a blob and build a local object URL to render in-app.
  const [previewUrls, setPreviewUrls] = useState({});  // { attachmentId: objectUrl }
  const previewUrlsRef = useRef({});
  const signaturePadRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // Calculate responsive scale for mobile
  useEffect(() => {
    let lastWidth = 0;
    const updateContainerWidth = () => {
      if (pdfContainerRef.current) {
        const width = pdfContainerRef.current.clientWidth;
        // Only recalculate if width actually changed (ignore height-only
        // changes from mobile address bar show/hide during scroll)
        if (Math.abs(width - lastWidth) < 2) return;
        lastWidth = width;
        setContainerWidth(width);
        
        // Auto-fit to container on mobile (when container is smaller than PDF)
        if (width < pdfWidth && width > 0) {
          const fitScale = (width - 32) / pdfWidth; // 32px padding
          setScale(Math.max(0.5, Math.min(1, fitScale)));
        }
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, [pdfWidth, pdfLoaded]);

  // Auto-save progress — debounced 3s after any field value change
  useEffect(() => {
    if (submitted || !token || Object.keys(fieldValues).length === 0) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        const fvArray = Object.entries(fieldValues).map(([field_id, value]) => ({
          field_id, value, prefilled: false,
        }));
        await axios.post(`${API_URL}/api/complete/${token}/save-progress`, {
          field_values: fvArray,
        });
        setAutoSaveStatus('saved');
        // Clear "Saved" indicator after 4s
        setTimeout(() => setAutoSaveStatus(prev => prev === 'saved' ? null : prev), 4000);
      } catch {
        setAutoSaveStatus('error');
      }
    }, 3000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues, submitted, token]);

  // Cascading dependency: when Country dropdown changes, reload State/County options
  useEffect(() => {
    if (!fields.length || !token) return;
    // Find the country field (mapped to address.countryID)
    const countryField = fields.find(f => {
      const bh = (f.bullhorn_field || f.bullhorn_source_field || f.mapped_bullhorn_field || '').toLowerCase();
      return bh === 'address.countryid';
    });
    if (!countryField) return;
    const countryValue = fieldValues[countryField.id];
    if (!countryValue) return;

    // Find any state fields (mapped to address.state)
    const stateFields = fields.filter(f => {
      const bh = (f.bullhorn_field || f.bullhorn_source_field || f.mapped_bullhorn_field || '').toLowerCase();
      return bh === 'address.state' || bh === 'secondaryaddress.state';
    });
    if (!stateFields.length) return;

    const docEntity = (document?.entity_type || 'candidate').toLowerCase();
    stateFields.forEach(sf => {
      const bhField = sf.bullhorn_field || sf.bullhorn_source_field || sf.mapped_bullhorn_field;
      axios.get(`${API_URL}/api/complete/${token}/field-options`, {
        params: { entity: docEntity, field: bhField, country_id: countryValue }
      }).then(({ data }) => {
        if (data?.options?.length) {
          setFields(prev => prev.map(f => {
            if (f.id !== sf.id) return f;
            return {
              ...f,
              options: data.options.map(o => o.value).filter(Boolean),
              bullhorn_picklist_options: data.options,
            };
          }));
        }
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length, token, ...fields.filter(f => {
    const bh = (f.bullhorn_field || f.bullhorn_source_field || f.mapped_bullhorn_field || '').toLowerCase();
    return bh === 'address.countryid';
  }).map(f => fieldValues[f.id])]);

  useEffect(() => {
    loadDocument();
  }, [token]);

  useEffect(() => {
    // Initialize signature pad when signature modal is open
    if (signatureFieldId && canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });
    }
  }, [signatureFieldId]);

  const loadDocument = async () => {
    try {
      const response = await publicApi.getDocument(token);
      setDocument(response.data.document);
      setTemplate(response.data.template);
      if (response.data.portal_token) {
        setPortalToken(response.data.portal_token);
      }

      let documentFields = response.data.fields || response.data.template?.fields || [];

      // Defensive remap: documents sent before the Skill→Category cascading
      // ID-rewrite fix have `filter_by_field_id` pointing at the *template*
      // field id (e.g. "field_xxx") instead of the doc-scoped field id
      // (e.g. "doc_field_xxx_yyy_3"). Both ids live on the field as `id`
      // and `original_id`, so we can repair the reference at load time.
      if (Array.isArray(documentFields) && documentFields.length) {
        const idMap = {};
        documentFields.forEach((f) => {
          if (f?.original_id) idMap[f.original_id] = f.id;
        });
        documentFields = documentFields.map((f) => {
          if (!f?.filter_by_field_id) return f;
          // Already points at a real doc field — leave alone.
          if (documentFields.some((g) => g.id === f.filter_by_field_id)) return f;
          // Otherwise rewrite via original_id.
          const remapped = idMap[f.filter_by_field_id];
          return remapped ? { ...f, filter_by_field_id: remapped } : f;
        });
      }

      // ATTACHMENT-ONLY MODE
      // The template has no PDF fields. Synthesize a single virtual
      // signature field so the existing signature → audit certificate →
      // submission plumbing flows through unchanged. The candidate signs
      // an acknowledgement after uploading their files.
      const docMode = response.data.document?.template_mode || response.data.template?.template_mode || 'form';
      if (docMode === 'attachment_only') {
        documentFields = [
          {
            id: 'attachment-only-acknowledgement',
            label: 'I confirm the uploaded documents are authentic and belong to me',
            field_type: 'signature',
            required: true,
            page: 1,
            position_x: 0,
            position_y: 0,
            width: 0,
            height: 0,
            candidate_editable: true,
          },
        ];
      }

      setFields(documentFields);
      setTotalPages(response.data.template?.page_count || 1);

      // Hydrate previously-uploaded attachments so files the candidate
      // uploaded in an earlier session re-appear (and stay previewable)
      // after a page reload. Without this, `attachments` starts empty and
      // only fills from in-session uploads, so a returning candidate sees
      // their CV "missing" and a required file_upload field reads as blank.
      let hydratedAttachments = {};
      try {
        const attRes = await axios.get(`${API_URL}/api/complete/${token}/attachments`);
        (attRes.data?.attachments || []).forEach((a) => {
          const fid = a.field_id;
          if (!fid) return;
          (hydratedAttachments[fid] = hydratedAttachments[fid] || []).push(a);
        });
        setAttachments(hydratedAttachments);
      } catch (e) {
        // Non-fatal — the form still works, the candidate just re-uploads.
      }

      // Backfill Bullhorn picklist options for any dropdown / radio /
      // checkbox-group field that was mapped before this feature shipped
      // (so its `options` array is still empty). One fetch per field, all
      // in parallel — silent failures keep the form usable as free-text.
      const docEntity = (response.data.document?.entity_type || 'candidate').toLowerCase();
      const fieldsNeedingOptions = documentFields.filter(f =>
        ['dropdown', 'radio_group', 'checkbox_group'].includes(f.field_type)
        && (!Array.isArray(f.options) || f.options.length === 0)
        && (f.bullhorn_field || f.bullhorn_source_field || f.mapped_bullhorn_field)
      );
      // Resolve prefilled values early so they can be used for option filtering.
      const prefilled = response.data.prefilled_values || {};

      if (fieldsNeedingOptions.length) {
        // Resolve the pre-filled country value so state/county dropdowns
        // are filtered by country on initial load (not just on change).
        const countryField = documentFields.find(ff => {
          const bh = (ff.bullhorn_field || ff.bullhorn_source_field || ff.mapped_bullhorn_field || '').toLowerCase();
          return bh === 'address.countryid';
        });
        const prefilledCountryId = countryField ? (prefilled[countryField.id] || '') : '';

        Promise.all(fieldsNeedingOptions.map(f => {
          const bhField = f.bullhorn_field || f.bullhorn_source_field || f.mapped_bullhorn_field;
          const bhLower = bhField.toLowerCase();
          const isStateField = bhLower === 'address.state' || bhLower === 'secondaryaddress.state';
          const params = { entity: docEntity, field: bhField };
          if (isStateField && prefilledCountryId) {
            params.country_id = prefilledCountryId;
          }
          return axios.get(`${API_URL}/api/complete/${token}/field-options`, {
            params
          }).then(({ data }) => ({ id: f.id, data })).catch(() => null);
        })).then(results => {
          const valid = results.filter(r => r?.data?.options?.length);
          if (!valid.length) return;
          setFields(prev => prev.map(f => {
            const hit = valid.find(v => v.id === f.id);
            if (!hit) return f;
            const optValues = hit.data.options.map(o => o.value).filter(Boolean);
            return {
              ...f,
              options: optValues,
              bullhorn_picklist_options: hit.data.options,
              bullhorn_picklist_multi: !!hit.data.multi,
              bullhorn_picklist_entity_backed: !!hit.data.entity_backed,
            };
          }));
        });
      }
      
      setPrefilledValues(prefilled);
      
      // Initialize field values. Priority order:
      //   1. Server-side prefill (Bullhorn → field mapping resolved at send time)
      //   2. Template `default_value` (recruiter-authored default — e.g.
      //      a radio group pre-selecting "Option 2")
      //   3. Type-appropriate empty (false for checkbox, '' otherwise)
      const initialValues = {};
      documentFields.forEach(field => {
        if (field.field_type === 'file_upload') {
          // Seed from hydrated attachments so a reloaded form keeps its
          // required-field validation satisfied (mirrors the in-session
          // upload handler which stores attachment ids in fieldValues).
          initialValues[field.id] = (hydratedAttachments[field.id] || []).map(a => a.id);
        } else if (prefilled[field.id] !== undefined && prefilled[field.id] !== '') {
          initialValues[field.id] = prefilled[field.id];
        } else if (field.default_value !== undefined && field.default_value !== '' && field.default_value !== null) {
          initialValues[field.id] = field.default_value;
        } else if (field.field_type === 'checkbox') {
          initialValues[field.id] = false;
        } else {
          initialValues[field.id] = '';
        }
      });
      setFieldValues(initialValues);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load document'));
    } finally {
      setLoading(false);
    }
  };

  const updateFieldValue = (fieldId, value) => {
    const field = fields.find(f => f.id === fieldId);
    if (field?.candidate_editable === false) {
      toast.error('This field cannot be modified');
      return;
    }
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  /**
   * Cascading skill options. When a recruiter has linked a Skills field
   * to a Category field via `filter_by_field_id`, the candidate's choice
   * of categories must filter the skills picklist live. We watch every
   * category-driver field and re-fetch the dependent field's options on
   * each change. Falls back to the unfiltered Bullhorn skill list when
   * the candidate hasn't picked anything yet.
   */
  useEffect(() => {
    if (!fields?.length) return;
    const dependents = fields.filter((f) => f.filter_by_field_id);
    if (!dependents.length) return;
    let cancelled = false;
    dependents.forEach(async (dep) => {
      const driverField = fields.find((f) => f.id === dep.filter_by_field_id);
      const driverValue = fieldValues[dep.filter_by_field_id];

      // Helper: resolve a raw value (id, label, or list) back to a CSV of
      // Bullhorn category IDs. Pre-filled values arrive as human labels
      // (e.g. "Groundworks & Civils Trades & Labour") because the
      // Bullhorn TO_ONE flatten returns the `.name` of the category.
      // Without this lookup the cascade fetches with a label, Bullhorn
      // ignores it, and the candidate sees the full unfiltered list.
      const labelToId = new Map();
      (driverField?.bullhorn_picklist_options || []).forEach((o) => {
        if (o?.label != null && o?.value != null) {
          labelToId.set(String(o.label).trim().toLowerCase(), String(o.value));
        }
      });
      const resolveOne = (raw) => {
        if (raw == null) return '';
        const s = String(raw).trim();
        if (!s) return '';
        if (/^\d+$/.test(s)) return s;             // already an id
        const hit = labelToId.get(s.toLowerCase());
        return hit || s;                            // fall back to raw so backend can still try
      };
      let ids;
      if (Array.isArray(driverValue)) {
        ids = driverValue.map(resolveOne).filter(Boolean).join(',');
      } else {
        ids = resolveOne(driverValue);
      }
      try {
        const res = await axios.get(
          `${API_URL}/api/complete/${token}/skill-options-by-category`,
          { params: { category_ids: ids } }
        );
        if (cancelled) return;
        const opts = res.data?.options || [];
        setFields((prev) => prev.map((f) =>
          f.id === dep.id
            ? {
                ...f,
                options: opts.map((o) => o.value),
                bullhorn_picklist_options: opts,
                bullhorn_picklist_multi: !!res.data?.multi,
                bullhorn_picklist_entity_backed: !!res.data?.entity_backed,
              }
            : f
        ));
        // Drop any previously-selected skill that's no longer in the
        // filtered list — but allow BOTH id matches and label matches so a
        // label-prefilled value (e.g. "Welfare Operative" from Bullhorn
        // TO_MANY flatten) survives the cascade if it's still applicable.
        const allowedIds = new Set(opts.map((o) => String(o.value)));
        const allowedLabels = new Set(opts.map((o) => String(o.label).trim().toLowerCase()));
        const isAllowed = (v) => {
          const s = String(v ?? '').trim();
          return s !== '' && (allowedIds.has(s) || allowedLabels.has(s.toLowerCase()));
        };
        setFieldValues((prev) => {
          const cur = prev[dep.id];
          if (cur === undefined || cur === null || cur === '') return prev;
          if (Array.isArray(cur)) {
            const kept = cur.filter(isAllowed);
            return kept.length === cur.length ? prev : { ...prev, [dep.id]: kept };
          }
          // Multi-select on this field is stored as a comma-CSV string
          // (the popover writes `selectedIds.join(',')`). Filter each id
          // independently so a multi-pick survives a cascade refresh.
          if (dep.bullhorn_picklist_multi && typeof cur === 'string' && cur.includes(',')) {
            const items = cur.split(',').map((s) => s.trim()).filter(Boolean);
            const kept = items.filter(isAllowed);
            if (kept.length === items.length) return prev;
            return { ...prev, [dep.id]: kept.join(',') };
          }
          return isAllowed(cur) ? prev : { ...prev, [dep.id]: '' };
        });
      } catch {
        // silent — the dependent dropdown stays usable with whatever
        // options it already had
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Re-run whenever any driver field's value changes
    JSON.stringify(fields.filter(f => f.filter_by_field_id).map(f => fieldValues[f.filter_by_field_id] ?? null)),
    fields?.length,
  ]);

  const uploadAttachment = async (fieldId, fieldLabel, file) => {
    if (!file) return;
    
    const MAX_SIZE = 10 * 1024 * 1024;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are supported');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('File too large (max 10MB)');
      return;
    }
    
    setUploadingFieldId(fieldId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field_id', fieldId);
      formData.append('field_label', fieldLabel || 'File Upload');
      
      const response = await axios.post(
        `${API_URL}/api/complete/${token}/upload-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setAttachments(prev => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), response.data]
      }));
      // Mark field as having a value for required-field validation
      setFieldValues(prev => ({
        ...prev,
        [fieldId]: [...((prev[fieldId] && Array.isArray(prev[fieldId])) ? prev[fieldId] : []), response.data.id]
      }));
      toast.success(`Uploaded ${response.data.filename}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setUploadingFieldId(null);
    }
  };

  const removeAttachment = async (fieldId, attachmentId) => {
    try {
      await axios.delete(`${API_URL}/api/complete/${token}/attachments/${attachmentId}`);
      setAttachments(prev => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter(a => a.id !== attachmentId)
      }));
      setFieldValues(prev => ({
        ...prev,
        [fieldId]: ((prev[fieldId] && Array.isArray(prev[fieldId])) ? prev[fieldId] : []).filter(id => id !== attachmentId)
      }));
      // Free the cached preview blob for the removed file.
      const objUrl = previewUrlsRef.current[attachmentId];
      if (objUrl) {
        URL.revokeObjectURL(objUrl);
        delete previewUrlsRef.current[attachmentId];
        setPreviewUrls(prev => {
          const next = { ...prev };
          delete next[attachmentId];
          return next;
        });
      }
      toast.success('Removed');
    } catch (err) {
      toast.error('Could not remove attachment');
    }
  };

  // Replace an existing attachment with a newer file: delete the old one,
  // then upload the new one into the same field. Wired to the existing
  // DELETE + upload endpoints — no new backend needed.
  const replaceAttachment = async (fieldId, fieldLabel, attachmentId, file) => {
    if (!file) return;
    await removeAttachment(fieldId, attachmentId);
    await uploadAttachment(fieldId, fieldLabel, file);
  };

  // Lazily fetch an attachment's bytes and build a local object URL so
  // images render inline and PDFs can be opened in-app. The download
  // endpoint serves Content-Disposition: attachment, so a plain <img src>
  // pointed at it would download rather than render — hence the blob fetch.
  const ensurePreviewUrl = useCallback(async (att) => {
    if (!att?.id || previewUrlsRef.current[att.id]) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/complete/${token}/attachments/${att.id}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      previewUrlsRef.current[att.id] = url;
      setPreviewUrls(prev => ({ ...prev, [att.id]: url }));
    } catch (e) {
      // Ignore — the filename + download link remain available.
    }
  }, [token]);

  // Prefetch previews for all current attachments (hydrated or freshly
  // uploaded) and revoke every object URL on unmount to avoid leaks.
  useEffect(() => {
    Object.values(attachments).flat().forEach((att) => {
      const ct = (att?.content_type || '').toLowerCase();
      const isPreviewable = ct.startsWith('image/') || ct === 'application/pdf'
        || /\.(png|jpe?g|pdf)$/i.test(att?.filename || '');
      if (isPreviewable) ensurePreviewUrl(att);
    });
  }, [attachments, ensurePreviewUrl]);

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
      previewUrlsRef.current = {};
    };
  }, []);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const saveSignature = () => {
    if (signatureMode === 'type') {
      if (!typedSignature.trim()) {
        toast.error('Please type your name');
        return;
      }
      // Convert typed name to signature image using canvas
      const canvas = window.document.createElement('canvas');
      canvas.width = 450;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 450, 150);
      ctx.fillStyle = 'black';
      ctx.font = 'italic 48px "Georgia", "Times New Roman", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature.trim(), 225, 75);
      const dataUrl = canvas.toDataURL();
      updateFieldValue(signatureFieldId, dataUrl);
      setSignatureFieldId(null);
      setTypedSignature('');
      setSignatureMode('draw');
    } else {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty() && signatureFieldId) {
        const dataUrl = signaturePadRef.current.toDataURL();
        updateFieldValue(signatureFieldId, dataUrl);
        setSignatureFieldId(null);
      } else {
        toast.error('Please draw your signature first');
      }
    }
  };

  const handleSubmit = async () => {
    // Block submission entirely when this is a recruiter preview.
    // The backend would accept it (preview docs are technically valid)
    // but submitting would leave dummy candidate data in the system.
    if (document?.preview_only) {
      toast.success('Preview mode — submit is disabled. Close this tab to return to the editor.');
      return;
    }

    const missingRequired = (template?.is_informational ? [] : fields)?.filter(field => {
      if (!field.required || field.candidate_editable === false) return false;
      if (field.field_type === 'file_upload') {
        return (attachments[field.id] || []).length === 0;
      }
      return !fieldValues[field.id];
    });
    
    if (missingRequired?.length > 0) {
      toast.error(`Please complete all required fields: ${missingRequired.map(f => f.label).join(', ')}`);
      const firstMissing = missingRequired[0];
      setCurrentPage(firstMissing.page || 1);
      setActiveFieldId(firstMissing.id);
      return;
    }

    setSubmitting(true);
    try {
      const submission = {
        field_values: Object.entries(fieldValues).map(([field_id, value]) => {
          const field = fields.find(f => f.id === field_id);
          // For file_upload fields, summarize attached filenames so the overlay shows them
          let finalValue = value;
          let attachmentIds;
          if (field?.field_type === 'file_upload') {
            const uploads = attachments[field_id] || [];
            finalValue = uploads.length > 0
              ? uploads.map(a => a.filename).join(', ')
              : '';
            // Make the attachment linkage explicit on the wire (in addition
            // to the filename string the overlay still reads). Attachments
            // are already linked server-side by document_id + field_id; this
            // gives the backend the exact ids without re-querying.
            attachmentIds = uploads.map(a => a.id);
          }
          return {
            field_id,
            value: finalValue,
            ...(attachmentIds ? { attachment_ids: attachmentIds } : {}),
            write_back_to_bullhorn: field?.write_back_to_bullhorn || false,
            bullhorn_field: field?.bullhorn_source_field || field?.mapped_bullhorn_field || '',
          };
        }),
      };
      
      await publicApi.submitDocument(token, submission);
      setSubmitted(true);
      toast.success('Document submitted successfully!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to submit document'));
    } finally {
      setSubmitting(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);
    setPdfLoaded(true);
  };

  // Jump-to-next-outstanding-required-field — the candidate gets a
  // single button that always moves them to the *next* field they need
  // to fill, in reading order (page → Y → X). Hugely speeds up
  // long-form completion (60-field Application Form) on desktop and is
  // critical on mobile where the candidate can't see the full
  // FieldStatusList sidebar.
  const jumpToOutstandingRequiredField = (direction = 'next') => {
    const outstanding = fields
      .filter(f => {
        if (!f.required || f.candidate_editable === false) return false;
        if (!shouldRenderField(f)) return false;
        if (f.field_type === 'file_upload') {
          return (attachments[f.id] || []).length === 0;
        }
        if (f.field_type === 'checkbox') return fieldValues[f.id] !== true;
        if (f.field_type === 'date' && f.auto_fill_completion_date) return false;
        const v = fieldValues[f.id];
        return !v || String(v).trim() === '';
      })
      .sort((a, b) => {
        const pa = a.page || 1, pb = b.page || 1;
        if (pa !== pb) return pa - pb;
        const ya = a.position_y ?? a.y ?? 0;
        const yb = b.position_y ?? b.y ?? 0;
        if (ya !== yb) return ya - yb;
        return (a.position_x ?? a.x ?? 0) - (b.position_x ?? b.x ?? 0);
      });

    if (outstanding.length === 0) {
      toast.success('All required fields complete — you can submit.');
      return;
    }

    // Find current position by activeFieldId, then pick the next/prev
    // outstanding field in reading order (wraps around).
    const currentIdx = outstanding.findIndex(f => f.id === activeFieldId);
    let target;
    if (currentIdx === -1) {
      target = outstanding[0];
    } else {
      const offset = direction === 'prev' ? -1 : 1;
      target = outstanding[(currentIdx + offset + outstanding.length) % outstanding.length];
    }

    setCurrentPage(target.page || 1);
    setActiveFieldId(target.id);
    setShowFieldsList(false);

    // PDF page swap is async — wait a tick before scrolling so the
    // field's overlay div has actually mounted.
    setTimeout(() => {
      const el = window.document.querySelector(`[data-testid="field-${target.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Briefly flash the field so it's visually obvious where we landed.
        el.style.transition = 'box-shadow 0.3s ease-in-out';
        el.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.6)';
        setTimeout(() => { el.style.boxShadow = ''; }, 1400);
        // Focus the inner input/select if there is one (keyboard users).
        const focusable = el.querySelector('input, select, textarea, [role="combobox"]');
        if (focusable && typeof focusable.focus === 'function') {
          try { focusable.focus({ preventScroll: true }); } catch (_e) { /* ignore unfocusable nodes */ }
        }
      }
    }, 80);
  };

  // Callback to get page dimensions when page loads
  const onPageLoadSuccess = (page) => {
    const { width } = page;
    setPdfWidth(width);
  };

  // Evaluate if a field's condition is met
  const isConditionMet = (field) => {
    if (!field.condition || !field.condition.field_id) return true;
    
    const sourceField = fields.find(f => f.id === field.condition.field_id);
    if (!sourceField) return true;
    
    const sourceValue = fieldValues[field.condition.field_id];
    const { operator, value: conditionValue } = field.condition;
    
    switch (operator) {
      case 'equals':
        return String(sourceValue || '').toLowerCase() === String(conditionValue || '').toLowerCase();
      case 'not_equals':
        return String(sourceValue || '').toLowerCase() !== String(conditionValue || '').toLowerCase();
      case 'contains':
        return String(sourceValue || '').toLowerCase().includes(String(conditionValue || '').toLowerCase());
      case 'is_checked':
        return sourceValue === true;
      case 'is_not_checked':
        return sourceValue !== true;
      case 'is_filled':
        return sourceValue !== undefined && sourceValue !== null && String(sourceValue).trim() !== '';
      case 'is_empty':
        return sourceValue === undefined || sourceValue === null || String(sourceValue).trim() === '';
      default:
        return true;
    }
  };

  // Evaluate the `disable_when` rule — same operator semantics as
  // `condition` but inverted intent: when this returns true, the field
  // is locked (read-only / non-interactive) rather than hidden. Used
  // to make mutually-exclusive tickbox pairs (Yes/No) where checking
  // one disables the other.
  const isFieldDisabledByRule = (field) => {
    const rule = field.disable_when;
    if (!rule || !rule.field_id) return false;
    const sourceField = fields.find(f => f.id === rule.field_id);
    if (!sourceField) return false;
    const sourceValue = fieldValues[rule.field_id];
    const { operator, value: ruleValue } = rule;
    switch (operator) {
      case 'equals':
        return String(sourceValue || '').toLowerCase() === String(ruleValue || '').toLowerCase();
      case 'not_equals':
        return String(sourceValue || '').toLowerCase() !== String(ruleValue || '').toLowerCase();
      case 'contains':
        return String(sourceValue || '').toLowerCase().includes(String(ruleValue || '').toLowerCase());
      case 'is_checked':
        return sourceValue === true;
      case 'is_not_checked':
        return sourceValue !== true;
      case 'is_filled':
        return sourceValue !== undefined && sourceValue !== null && String(sourceValue).trim() !== '';
      case 'is_empty':
        return sourceValue === undefined || sourceValue === null || String(sourceValue).trim() === '';
      default:
        return false;
    }
  };

  // Hide a field from the candidate's view when it's locked (not editable)
  // AND has no value from Bullhorn. There's nothing for the candidate to
  // do with such a field, and the empty placeholder ("New Number" etc.)
  // just adds visual noise to the form. Recruiters can opt out per-field
  // via `field.always_show_when_empty = true` if they need the box to
  // remain visible (e.g. a "Will Be Provided" placeholder by design).
  // Auto-fill-on-completion date fields are never hidden — they're locked
  // but display today's date, which is meaningful information.
  const shouldRenderField = (field) => {
    if (!isConditionMet(field)) return false;
    if (field.candidate_editable !== false) return true;
    if (field.always_show_when_empty) return true;
    if (field.field_type === 'date' && field.auto_fill_completion_date) return true;
    const v = fieldValues[field.id];
    if (field.field_type === 'checkbox') return v === true;
    return v !== undefined && v !== null && String(v).trim() !== '';
  };

  // Get fields for current page (filtered by conditions + locked-empty hide rule)
  const currentPageFields = fields
    .filter(f => (f.page || 1) === currentPage)
    .filter(shouldRenderField);
  
  // Calculate completion stats (only for visible/condition-met fields)
  const visibleFields = fields.filter(f => isConditionMet(f));
  const editableFields = visibleFields.filter(f => f.candidate_editable !== false);
  // Informational templates skip the required-field gate entirely — the
  // candidate is acknowledging the document, not filling it in.
  const isInformational = !!template?.is_informational;
  const completedFields = editableFields.filter(f => {
    const value = fieldValues[f.id];
    if (f.field_type === 'checkbox') return value === true;
    return value && String(value).trim() !== '';
  });
  const requiredFields = editableFields.filter(f => f.required);
  const completedRequiredFields = requiredFields.filter(f => {
    const value = fieldValues[f.id];
    // Auto-fill-with-completion-date fields are stamped server-side at submit,
    // so they don't need a value from the candidate to count as complete.
    if (f.field_type === 'date' && f.auto_fill_completion_date) return true;
    if (f.field_type === 'checkbox') return value === true;
    return value && String(value).trim() !== '';
  });
  
  const progress = editableFields.length > 0 
    ? Math.round((completedFields.length / editableFields.length) * 100) 
    : 100;
  const canSubmit = isInformational || completedRequiredFields.length === requiredFields.length;

  // Render inline field on PDF
  const renderPdfField = (field) => {
    // Auto-fill-with-completion-date locks the field on the candidate
    // side — the value is stamped server-side at submission so they can't
    // type something different. Display "today" so they know what'll
    // land in the field.
    const isAutoCompletion = field.field_type === 'date' && field.auto_fill_completion_date;
    // `isLocked` already covers the recruiter-set "not editable" case and
    // auto-completion dates. Now also fold in the runtime `disable_when`
    // rule so e.g. a mutually-exclusive "Yes" tickbox locks the "No" box
    // the moment the candidate ticks "Yes".
    const isLockedByRule = isFieldDisabledByRule(field);
    const isLocked = field.candidate_editable === false || isAutoCompletion || isLockedByRule || isInformational;
    const rawValue = fieldValues[field.id];
    const value = isAutoCompletion && (!rawValue || String(rawValue).trim() === '')
      ? new Date().toLocaleDateString('en-GB')  // dd/mm/yyyy
      : rawValue;
    const isActive = activeFieldId === field.id;
    const isComplete = field.field_type === 'checkbox' ? value === true : value && String(value).trim() !== '';

    // Auto-shrink font size to keep long Bullhorn-prefilled values inside
    // the field box (so a long address / certifications list doesn't get
    // truncated off the right edge). Approx 0.55 × fontSize per char for
    // Helvetica — if the computed width exceeds available space, scale
    // proportionally down to a 7px floor (still readable).
    // On iOS, inputs with font-size < 16px trigger auto-zoom — to prevent
    // that, we set the input font-size to 16px and use CSS transform to
    // visually shrink it back to the computed size.
    const fieldWidthPx = (field.width || 150) * scale;
    const fieldHeightPx = (field.height || 24) * scale;
    const baseFontPx = Math.max(10, 12 * scale);
    const valueLen = value ? String(value).length : 0;
    const usableWidth = Math.max(1, fieldWidthPx - 12);
    // How many lines of text fit vertically in the field box
    const linesInBox = Math.max(1, Math.floor(fieldHeightPx / (baseFontPx * 1.2)));
    // Total character capacity = chars per line × number of lines
    const charsPerLine = Math.floor(usableWidth / (baseFontPx * 0.55));
    const charsAtBase = charsPerLine * linesInBox;
    const dynamicFontPx = valueLen > charsAtBase && charsAtBase > 0
      ? Math.max(baseFontPx * 0.7, Math.floor(baseFontPx * (charsAtBase / valueLen) * 100) / 100)
      : baseFontPx;
    // Prevent iOS Safari input zoom: always render the <input> at 16px and
    // use a CSS transform to shrink it visually to the desired size.
    const iosSafeFontPx = 16;
    const inputScaleFactor = dynamicFontPx < iosSafeFontPx ? dynamicFontPx / iosSafeFontPx : 1;
    const actualInputFontPx = dynamicFontPx < iosSafeFontPx ? iosSafeFontPx : dynamicFontPx;

    const fieldStyle = {
      position: 'absolute',
      left: `${(field.position_x || field.x || 0) * scale}px`,
      top: `${(field.position_y || field.y || 0) * scale}px`,
      width: inputScaleFactor < 1 ? `${(field.width || 150) * scale / inputScaleFactor}px` : `${(field.width || 150) * scale}px`,
      height: inputScaleFactor < 1 ? `${(field.height || 24) * scale / inputScaleFactor}px` : `${(field.height || 24) * scale}px`,
      fontSize: `${actualInputFontPx}px`,
      transform: inputScaleFactor < 1 ? `scale(${inputScaleFactor})` : undefined,
      transformOrigin: inputScaleFactor < 1 ? 'left top' : undefined,
      // Horizontal text alignment is recruiter-configurable per field
      // (left / center / right) and must be reflected on-screen as well
      // as in the final PDF — otherwise the toggle visually "does
      // nothing" during the Preview as Candidate flow.
      textAlign: field.text_alignment || 'left',
      zIndex: isActive ? 20 : 10,
    };

    const baseClasses = `transition-all duration-150 ${isActive ? 'ring-2 ring-blue-500 ring-offset-1' : ''} ${!isComplete && field.required && !isLocked ? 'border-amber-400' : ''}`;

    // Checkbox
    if (field.field_type === 'checkbox') {
      const tx = field.transparent_background;
      return (
        <div
          key={field.id}
          style={{...fieldStyle, width: `${24 * scale}px`, height: `${24 * scale}px`}}
          onClick={() => !isLocked && updateFieldValue(field.id, !value)}
          className={cn(
            baseClasses,
            'flex items-center justify-center rounded cursor-pointer',
            tx ? 'border-0' : 'border-2',
            tx ? '' :
              (isLocked ? 'bg-amber-100 border-amber-300 cursor-not-allowed' :
              value ? 'bg-green-100 border-green-500' : 'bg-white border-blue-400 hover:bg-blue-50')
          )}
          data-testid={`field-${field.id}`}
        >
          {value && <Check className={tx ? 'text-emerald-700' : 'text-green-600'} style={{width: 16 * scale, height: 16 * scale}} />}
          {isLocked && <Lock className="text-amber-600" style={{width: 12 * scale, height: 12 * scale}} />}
        </div>
      );
    }

    // Signature
    if (field.field_type === 'signature') {
      return (
        <div
          key={field.id}
          style={fieldStyle}
          onClick={() => !isLocked && !value && setSignatureFieldId(field.id)}
          className={`${baseClasses} flex items-center justify-center rounded border-2 ${
            isLocked ? 'bg-amber-100 border-amber-300 cursor-not-allowed' :
            value ? 'bg-white border-green-500' : 'bg-blue-50 border-blue-400 hover:bg-blue-100 cursor-pointer'
          }`}
          data-testid={`field-${field.id}`}
        >
          {value ? (
            <div className="relative w-full h-full p-1">
              <img src={value} alt="Signature" className="w-full h-full object-contain" />
              {!isLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); updateFieldValue(field.id, ''); }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : isLocked ? (
            <Lock className="text-amber-600" style={{width: 16 * scale, height: 16 * scale}} />
          ) : (
            <span className="text-blue-600 font-medium text-xs">Click to sign</span>
          )}
        </div>
      );
    }

    // Date
    if (field.field_type === 'date') {
      // Helper to safely parse date value (handles Bullhorn timestamps + UK strings)
      const safeParseDate = (dateValue) => {
        if (!dateValue) return null;
        try {
          // Handle Bullhorn timestamps (milliseconds / seconds)
          if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
            const timestamp = parseInt(dateValue);
            if (timestamp > 1000000000000) {
              return new Date(timestamp);
            }
            return new Date(timestamp * 1000);
          }
          // UK-format strings (DD/MM/YYYY or DD/MM/YY).  JavaScript's
          // `new Date("01/06/2026")` parses as MM/DD/YYYY (Jan 6 in en-US)
          // which silently reverses the date — the candidate then sees
          // "06/01/2026" after we format back as `dd/MM/yyyy`.  Detect
          // the slash-separated form explicitly and build the Date from
          // its components so the round-trip is stable.
          if (typeof dateValue === 'string') {
            const ukMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
            if (ukMatch) {
              const day = parseInt(ukMatch[1], 10);
              const month = parseInt(ukMatch[2], 10);
              let year = parseInt(ukMatch[3], 10);
              if (year < 100) year += 2000;
              // Sanity: month must be 1-12, day must be 1-31.
              if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return new Date(year, month - 1, day);
              }
            }
          }
          // ISO date strings (2026-06-01, 2026-06-01T...) fall through
          // to native parse — these are unambiguous.
          const date = new Date(dateValue);
          return isNaN(date.getTime()) ? null : date;
        } catch {
          return null;
        }
      };
      
      const dateObj = safeParseDate(value);
      const previewDateObj = dateObj || (isAutoCompletion ? new Date() : null);
      // UK format DD/MM/YYYY (was MM/DD/YY) — Clements Young + other UK
      // agencies were reading 06/08/26 as 6th August, not 8th June.
      // 4-digit year so candidates can clearly see the year on dense
      // payroll/holiday forms.
      const displayValue = previewDateObj ? format(previewDateObj, 'dd/MM/yyyy') : '-';
      
      if (isLocked) {
        const blend = field.blend_into_document;
        return (
          <div
            key={field.id}
            // When blending, drop the inline amber background and use a
            // transparent box so the value reads as native body copy.
            // Mirrors the locked text-field branch above for consistency.
            style={blend ? fieldStyle : { ...fieldStyle, backgroundColor: '#fffbeb' }}
            className={`${baseClasses} flex items-center px-1.5 ${
              blend
                ? 'text-black'
                : 'rounded border border-amber-200'
            }`}
            data-testid={`field-${field.id}`}
          >
            {/* Calendar icon is a date-field affordance — also hidden in
                blend mode so nothing visually screams "form field". */}
            {!blend && <CalendarIcon className="w-2.5 h-2.5 mr-1 text-amber-600 flex-shrink-0" />}
            <span className={`truncate flex-1 min-w-0 ${blend ? 'text-black' : 'text-amber-900'}`}>
              {displayValue}
            </span>
          </div>
        );
      }
      return (
        <Popover key={field.id}>
          <PopoverTrigger asChild>
            <div
              style={{ ...fieldStyle, backgroundColor: dateObj ? '#f0fdf4' : '#ffffff' }}
              className={`${baseClasses} flex items-center px-1.5 rounded border-2 cursor-pointer ${
                dateObj ? 'border-green-500' : 'border-blue-400 hover:bg-blue-50'
              }`}
              data-testid={`field-${field.id}`}
            >
              {/* Smaller icon + tighter padding so the date string has the
                  same visual weight as adjacent text inputs (Name, NI etc). */}
              <CalendarIcon className="w-2.5 h-2.5 mr-1 text-muted-foreground flex-shrink-0" />
              <span className={`truncate flex-1 min-w-0 ${dateObj ? 'text-foreground' : 'text-muted-foreground'}`}>
                {displayValue !== '-' ? displayValue : 'Select'}
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-[100] bg-white shadow-xl min-w-[280px]" align="start" sideOffset={4} style={{pointerEvents: 'auto'}} avoidCollisions>
            <Calendar
              mode="single"
              selected={dateObj || undefined}
              onSelect={(date) => {
                if (!date) { updateFieldValue(field.id, null); return; }
                // Format as YYYY-MM-DD in the user's local timezone to prevent
                // off-by-one-day when .toISOString() shifts midnight local→UTC
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                updateFieldValue(field.id, `${yyyy}-${mm}-${dd}`);
              }}
              weekStartsOn={1}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      );
    }

    // Dropdown
    if (field.field_type === 'dropdown') {
      // When the field is a Bullhorn-backed picklist, `bullhorn_picklist_options`
      // carries the {value, label} pairs (label is human-readable, value is
      // the ID written back to Bullhorn). Fall back to the plain `options`
      // array (recruiter-authored text options).
      const picklistPairs = Array.isArray(field.bullhorn_picklist_options) && field.bullhorn_picklist_options.length
        ? field.bullhorn_picklist_options
        : (field.options || []).map((opt) => ({ value: opt, label: opt }));
      const labelByValue = Object.fromEntries(picklistPairs.map((p) => [String(p.value), p.label]));
      const isMulti = !!field.bullhorn_picklist_multi;

      // Build a reverse map: label (lowercased) → value (ID string), so
      // we can normalise legacy prefilled values that were stored as
      // human labels instead of picklist IDs.
      const valueByLabel = Object.fromEntries(
        picklistPairs.map((p) => [String(p.label).trim().toLowerCase(), String(p.value)])
      );

      // Multi-select branch — render a checkbox popover so the candidate
      // can tick multiple Bullhorn TO_MANY entries (skills, certifications,
      // categories). The stored value is a comma-joined list of IDs so the
      // write-back path can split it back into `[{id: N}, ...]`.
      if (isMulti) {
        const selectedIds = (() => {
          if (Array.isArray(value)) return value.map(String);
          if (value == null || value === '') return [];
          return String(value).split(',').map((v) => v.trim()).filter(Boolean);
        })().map((raw) => {
          // Normalise: if `raw` is a label (not a known ID), translate to ID
          if (labelByValue[raw]) return raw; // already an ID
          return valueByLabel[raw.toLowerCase()] || raw;
        });
        const selectedLabels = selectedIds.map((id) => labelByValue[id] ?? id);
        const displaySummary = selectedLabels.length === 0
          ? ''
          : selectedLabels.length <= 2
            ? selectedLabels.join(', ')
            : `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;

        if (isLocked) {
          return (
            <div
              key={field.id}
              style={fieldStyle}
              className={`${baseClasses} flex items-center px-1.5 rounded border bg-amber-50 border-amber-200`}
              data-testid={`field-${field.id}`}
            >
              <span className="truncate text-amber-900 flex-1 min-w-0">{selectedLabels.join(', ') || '-'}</span>
            </div>
          );
        }

        const toggleId = (id) => {
          const next = selectedIds.includes(id)
            ? selectedIds.filter((x) => x !== id)
            : [...selectedIds, id];
          updateFieldValue(field.id, next.join(','));
        };

        return (
          <div key={field.id} style={fieldStyle} className="relative">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`${baseClasses} w-full h-full text-left text-xs rounded border-2 px-2 flex items-center justify-between ${
                    selectedIds.length ? 'bg-green-50 border-green-500' : 'bg-white border-blue-400'
                  }`}
                  style={{ fontSize: `${Math.max(10, 12 * scale)}px` }}
                  data-testid={`field-${field.id}`}
                >
                  <span className="truncate">
                    {displaySummary || <span className="text-muted-foreground">Select…</span>}
                  </span>
                  <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0 opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-72 max-h-80 overflow-hidden z-[100]" align="start">
                <MultiSelectSearchPanel
                  fieldId={field.id}
                  options={picklistPairs}
                  selectedIds={selectedIds}
                  toggleId={toggleId}
                  onClear={() => updateFieldValue(field.id, '')}
                />
              </PopoverContent>
            </Popover>
          </div>
        );
      }

      // Normalise value: if it's a label string (not a known picklist ID),
      // translate to the matching ID so the Select highlights correctly.
      const normalisedValue = (() => {
        if (!value) return '';
        const vs = String(value);
        if (labelByValue[vs]) return vs; // already an ID
        return valueByLabel[vs.toLowerCase()] || vs;
      })();
      const displayValue = normalisedValue ? (labelByValue[normalisedValue] ?? normalisedValue) : '';

      if (isLocked) {
        return (
          <div
            key={field.id}
            style={fieldStyle}
            className={`${baseClasses} flex items-center px-1.5 rounded border bg-amber-50 border-amber-200`}
            data-testid={`field-${field.id}`}
          >
            <span className="truncate text-amber-900 flex-1 min-w-0">{displayValue || '-'}</span>
          </div>
        );
      }
      // For dropdowns with many options, use a searchable popover
      const useSearchable = picklistPairs.length > 8;
      if (useSearchable) {
        return (
          <SearchableDropdown
            key={field.id}
            fieldId={field.id}
            fieldStyle={fieldStyle}
            baseClasses={baseClasses}
            scale={scale}
            value={normalisedValue}
            displayValue={displayValue}
            options={picklistPairs}
            onChange={(v) => updateFieldValue(field.id, v)}
          />
        );
      }
      return (
        <div key={field.id} style={fieldStyle} className="relative">
          <Select value={normalisedValue || ''} onValueChange={(v) => updateFieldValue(field.id, v)}>
            <SelectTrigger
              className={`${baseClasses} w-full h-full text-xs rounded border-2 ${
                value ? 'bg-green-50 border-green-500' : 'bg-white border-blue-400'
              }`}
              style={{ fontSize: `${Math.max(10, 12 * scale)}px` }}
              data-testid={`field-${field.id}`}
            >
              <SelectValue placeholder="Select...">{displayValue || undefined}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {picklistPairs.map((opt) => (
                <SelectItem key={String(opt.value)} value={String(opt.value)}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Radio Group (single selection from options)
    if (field.field_type === 'radio_group') {
      const picklistPairs = Array.isArray(field.bullhorn_picklist_options) && field.bullhorn_picklist_options.length
        ? field.bullhorn_picklist_options
        : (field.options || []).map((opt) => ({ value: opt, label: opt }));
      const labelByValue = Object.fromEntries(picklistPairs.map((p) => [String(p.value), p.label]));

      // Auto-collapse to a dropdown when the radio layout can't physically
      // fit. Bullhorn-mapped fields (e.g. an "Umbrella" customtext with a
      // valueList) often have 5-10+ option values, but the recruiter's
      // field box on the PDF is sized for a single text input — the options
      // then overlap. Falling back to a dropdown keeps the data identical
      // (one selected value, same options list) but renders compactly.
      // Recruiters who explicitly want radios can use the "custom" layout
      // (with per-option drag positions) which we always honour, or make
      // the field box tall enough.
      // 2-option Yes/No / True/False fields are never collapsed — short
      // labels always fit and recruiters expect to see both choices at
      // once (e.g. "Do you have any unspent criminal convictions").
      const layout = field.options_layout || 'vertical';
      const positions = field.option_positions || {};
      const PER_OPTION_PX = 18;     // approximate row height at base zoom
      const fieldH = field.height || 0;
      const fieldW = field.width || 0;
      const needsHeight = picklistPairs.length * PER_OPTION_PX;
      const wontFit = layout === 'vertical'
        ? needsHeight > fieldH + 4
        : (layout === 'horizontal' ? (PER_OPTION_PX * 4) > fieldW : false);
      const useDropdownFallback =
        layout !== 'custom' &&
        wontFit &&
        picklistPairs.length > 2;
      if (useDropdownFallback) {
        const displayValue = value ? (labelByValue[String(value)] ?? value) : '';
        if (isLocked) {
          return (
            <div
              key={field.id}
              style={fieldStyle}
              className={`${baseClasses} flex items-center px-1.5 rounded border bg-amber-50 border-amber-200`}
              data-testid={`field-${field.id}`}
            >
              <span className="truncate text-amber-900 flex-1 min-w-0">{displayValue || '-'}</span>
            </div>
          );
        }
        return (
          <select
            key={field.id}
            style={fieldStyle}
            value={value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className={`${baseClasses} rounded border-2 px-1 cursor-pointer ${
              value ? 'bg-green-50 border-green-500' : 'bg-white border-blue-400'
            }`}
            data-testid={`field-${field.id}`}
          >
            <option value="">Select…</option>
            {picklistPairs.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      // Compute per-option offsets. The recruiter chose one of three layouts
      // in the editor (vertical / horizontal / custom). Render each radio
      // button at its computed offset so what the candidate sees matches
      // exactly what was authored.
      const w = field.width || 0;
      const h = field.height || 0;
      const computeOffset = (label, idx, total) => {
        if (layout === 'custom' && positions[label]) return positions[label];
        if (layout === 'horizontal' && total > 0) {
          const slot = w / total;
          return { x: slot * idx + 4, y: Math.max(4, h / 2 - 8) };
        }
        const slot = total > 0 ? h / total : h;
        return { x: 4, y: slot * idx + Math.max(0, (slot - 14) / 2) };
      };

      if (isLocked) {
        return (
          <div
            key={field.id}
            style={fieldStyle}
            className={`${baseClasses} flex items-center px-1.5 rounded border bg-amber-50 border-amber-200`}
            data-testid={`field-${field.id}`}
          >
            <span className="truncate text-amber-900 flex-1 min-w-0">{(value && labelByValue[String(value)]) || value || '-'}</span>
          </div>
        );
      }
      // On mobile (small scale), the absolute-positioned radio options
      // overflow the narrow field and labels get clipped. Switch to a
      // simple vertical flow layout that wraps text properly.
      const isMobileRadio = scale < 0.75;
      return (
        <div
          key={field.id}
          style={isMobileRadio
            ? { ...fieldStyle, height: 'auto', minHeight: fieldStyle.height }
            : fieldStyle
          }
          className={cn(
            baseClasses,
            'rounded',
            isMobileRadio && 'overflow-visible',
            field.transparent_background
              ? 'border-0'
              : ['border-2', value ? 'bg-green-50/40 border-green-500' : 'bg-blue-50/30 border-blue-400']
          )}
          data-testid={`field-${field.id}`}
        >
          {isMobileRadio ? (
            // Mobile: simple stacked list, no absolute positioning
            <div className="flex flex-col gap-1 p-1 w-full">
              {picklistPairs.map((opt, idx) => {
                const optValue = String(opt.value);
                const isSelected = String(value) === optValue;
                const hideLabel = field.hide_option_labels;
                return (
                  <button
                    key={optValue}
                    type="button"
                    className={cn(
                      'flex items-start gap-2 px-1.5 py-1 rounded transition-colors text-left w-full',
                      field.transparent_background
                        ? (isSelected ? '' : 'hover:bg-blue-100/30')
                        : (isSelected ? 'bg-green-100/80' : 'hover:bg-blue-100/60')
                    )}
                    style={{ fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateFieldValue(field.id, optValue);
                    }}
                    data-testid={`radio-${field.id}-${idx}`}
                    title={hideLabel ? opt.label : undefined}
                  >
                    <span
                      className={`rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'border-green-600 bg-green-600' : 'border-blue-400 bg-white'
                      }`}
                      style={{ width: 14, height: 14 }}
                    >
                      {isSelected && <span className="rounded-full bg-white" style={{ width: 8, height: 8 }} />}
                    </span>
                    {!hideLabel && (
                      <span className="leading-snug">{opt.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            // Desktop: absolute positioning matching PDF layout
            picklistPairs.map((opt, idx) => {
              const optValue = String(opt.value);
              const isSelected = String(value) === optValue;
              const off = computeOffset(opt.label, idx, picklistPairs.length);
              const hideLabel = field.hide_option_labels;
              return (
                <button
                  key={optValue}
                  type="button"
                  className={cn(
                    'absolute flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-colors',
                    field.transparent_background
                      ? (isSelected ? '' : 'hover:bg-blue-100/30')
                      : (isSelected ? 'bg-green-100/80' : 'hover:bg-blue-100/60')
                  )}
                  style={{
                    left: off.x * scale,
                    top: off.y * scale,
                    fontSize: `${Math.max(10, 12 * scale)}px`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFieldValue(field.id, optValue);
                  }}
                  data-testid={`radio-${field.id}-${idx}`}
                  title={hideLabel ? opt.label : undefined}
                >
                  <span
                    className={`rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-green-600 bg-green-600' : 'border-blue-400 bg-white'
                    }`}
                    style={{ width: Math.max(10, 14 * scale), height: Math.max(10, 14 * scale) }}
                  >
                    {isSelected && <span className="rounded-full bg-white" style={{ width: Math.max(5, 8 * scale), height: Math.max(5, 8 * scale) }} />}
                  </span>
                  {!hideLabel && (
                    <span className="leading-none whitespace-nowrap">{opt.label}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      );
    }

    // File Upload
    if (field.field_type === 'file_upload') {
      const uploads = attachments[field.id] || [];
      const isUploading = uploadingFieldId === field.id;
      const canUploadMore = uploads.length < 3 && !isLocked;
      
      return (
        <div
          key={field.id}
          style={{...fieldStyle, height: 'auto', minHeight: `${Math.max(60, field.height || 80) * scale}px`}}
          className={`${baseClasses} rounded-lg border-2 border-dashed p-2 ${
            isLocked ? 'bg-amber-50 border-amber-300' :
            uploads.length > 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-blue-50/60 border-blue-400'
          }`}
          data-testid={`field-${field.id}`}
          onFocus={() => setActiveFieldId(field.id)}
        >
          {uploads.length > 0 && (
            <div className="space-y-1 mb-2" style={{fontSize: `${Math.max(9, 10*scale)}px`}}>
              {uploads.map(att => {
                const ct = (att.content_type || '').toLowerCase();
                const isImage = ct.startsWith('image/') || /\.(png|jpe?g)$/i.test(att.filename || '');
                const isPdf = ct === 'application/pdf' || /\.pdf$/i.test(att.filename || '');
                const previewUrl = previewUrls[att.id];
                return (
                  <div key={att.id} className="bg-white rounded px-1 py-0.5 border">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                      <span className="truncate flex-1" title={att.filename}>{att.filename}</span>
                      {previewUrl && (
                        <a
                          href={previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-800 p-0.5"
                          title="Open preview"
                          data-testid={`preview-attachment-${att.id}`}
                        >
                          <ZoomIn className="w-3 h-3" />
                        </a>
                      )}
                      {!isLocked && (
                        <label
                          className="text-amber-600 hover:text-amber-800 p-0.5 cursor-pointer"
                          title="Replace file"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`replace-attachment-${att.id}`}
                        >
                          <RefreshCw className="w-3 h-3" />
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                            className="hidden"
                            disabled={isUploading}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) replaceAttachment(field.id, field.label, att.id, f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                      {!isLocked && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeAttachment(field.id, att.id); }}
                          className="text-red-500 hover:text-red-700 p-0.5"
                          data-testid={`remove-attachment-${att.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {isImage && previewUrl && (
                      <img
                        src={previewUrl}
                        alt={att.filename}
                        className="mt-1 max-h-24 w-auto rounded border object-contain"
                        data-testid={`preview-thumb-${att.id}`}
                      />
                    )}
                    {isPdf && previewUrl && (
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline"
                        data-testid={`preview-pdf-link-${att.id}`}
                      >
                        <FileText className="w-3 h-3" /> Open PDF
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {canUploadMore && (
            <label
              className="flex items-center justify-center gap-1 cursor-pointer text-blue-600 hover:text-blue-700"
              style={{fontSize: `${Math.max(10, 12*scale)}px`}}
              data-testid={`upload-${field.id}`}
            >
              {isUploading ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Uploading…</span>
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3" />
                  <span>{uploads.length > 0 ? `Add file (${uploads.length}/3)` : 'Click to upload'}</span>
                </>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                className="hidden"
                disabled={isUploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadAttachment(field.id, field.label, f);
                  e.target.value = '';
                }}
              />
            </label>
          )}
          {isLocked && uploads.length === 0 && (
            <div className="text-xs text-amber-700 text-center">Upload disabled</div>
          )}
        </div>
      );
    }

    // Text, Email, Phone, Number
    // For LOCKED fields with a value, render as a wrapping <div> instead
    // of a single-line <input>. Long Bullhorn-prefilled values (e.g. a
    // Health & Safety description) would otherwise be truncated with an
    // ellipsis at the field box edge, hiding important information from
    // the candidate. To avoid the wrapped block ballooning vertically
    // beyond the recruiter's positioned height (and visually "overflowing"
    // the form layout), we estimate how many lines the text needs at the
    // base font size and shrink the font proportionally so the content
    // FITS inside the recruiter's original box. Mirrors `pdf_service._draw_text`
    // logic so the PDF and the on-screen preview stay visually consistent.
    // Format a number-field value for display. Mirrors the logic in
    // backend/pdf_service.py `_format_number_value` so the candidate
    // sees the same shape they'll get on the final PDF. Returns the
    // raw value untouched for non-number fields or empty values.
    const formatNumberForDisplay = (rawValue) => {
      if (field.field_type !== 'number') return rawValue;
      if (rawValue === null || rawValue === undefined || rawValue === '') return rawValue;
      const symbol = (field.currency_symbol || '').trim();
      const position = (field.currency_position || 'prefix').toLowerCase();
      // Default to "money" formatting (commas + 2dp) only when the
      // recruiter explicitly enabled it OR when a currency symbol is
      // present and the toggle hasn't been turned off. Plain integer
      // fields without a symbol stay as-is.
      const fmtMoney = field.currency_format_money === true ||
        (symbol && field.currency_format_money !== false);
      let raw = String(rawValue).trim();
      if (symbol && raw.startsWith(symbol)) raw = raw.slice(symbol.length).trim();
      if (symbol && raw.endsWith(symbol)) raw = raw.slice(0, -symbol.length).trim();
      const cleaned = raw.replace(/,/g, '');
      const num = Number(cleaned);
      let text;
      if (Number.isFinite(num)) {
        if (fmtMoney) text = num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        else if (Number.isInteger(num)) text = num.toLocaleString('en-GB');
        else text = String(num); // preserves whatever precision the user typed
      } else {
        text = String(rawValue);
      }
      if (!symbol) return text;
      return position === 'suffix' ? `${text}${symbol}` : `${symbol}${text}`;
    };

    if (isLocked && value && String(value).trim() !== '') {
      const fieldHeightPx = (field.height || 24) * scale;
      const fieldWidthPxLocal = (field.width || 150) * scale;
      // Estimate line capacity at base font (12px Helvetica): ~0.55em per
      // char. Compute how many lines we'd need; shrink font so all lines
      // fit inside fieldHeightPx (with line-height 1.2).
      const charsPerLineAtBase = Math.max(1, Math.floor((fieldWidthPxLocal - 8) / (baseFontPx * 0.55)));
      const linesAtBase = Math.max(1, Math.ceil(valueLen / charsPerLineAtBase));
      const maxLinesInBox = Math.max(1, Math.floor(fieldHeightPx / (baseFontPx * 1.2)));
      let wrapFontPx = baseFontPx;
      if (linesAtBase > maxLinesInBox) {
        // Shrink font so wrapped text fits inside the field box.
        // Floor at 83% of base (≈10px) so locked pre-filled values
        // stay visually consistent with other fields (max 2px gap).
        const ratio = Math.sqrt(maxLinesInBox / linesAtBase);
        wrapFontPx = Math.max(baseFontPx * 0.83, Math.floor(baseFontPx * ratio * 100) / 100);
      }
      return (
        <div
          key={field.id}
          style={fieldStyle}
          className={`${baseClasses} px-1.5 overflow-hidden ${
            // Vertical alignment: top (default), middle, bottom
            (field.text_vertical_alignment || 'top') === 'middle' ? 'flex items-center' :
            (field.text_vertical_alignment || 'top') === 'bottom' ? 'flex items-end' :
            'flex items-start'
          } ${
            field.blend_into_document
              // Seamless mode — strip border/background so the prefilled
              // value reads as native body copy on the underlying PDF.
              // Black text only; no rounded corner / no border / no fill.
              ? 'text-black'
              : 'rounded border bg-amber-50 border-amber-200 text-amber-900'
          }`}
          data-testid={`field-${field.id}`}
          title={value}
        >
          <div
            className="break-words whitespace-pre-wrap leading-tight w-full"
            style={{ fontSize: `${wrapFontPx}px`, lineHeight: 1.2 }}
          >
            {formatNumberForDisplay(value)}
          </div>
        </div>
      );
    }

    // Number fields with a currency symbol get an input + adornment
    // wrapper so the candidate sees the £/$/€ inline. We keep the
    // raw numeric string in state — the symbol is purely cosmetic.
    if (field.field_type === 'number' && field.currency_symbol) {
      const symbol = field.currency_symbol;
      const isPrefix = (field.currency_position || 'prefix') === 'prefix';
      return (
        <div
          key={field.id}
          style={fieldStyle}
          className={`${baseClasses} rounded border outline-none flex items-stretch overflow-hidden ${
            isLocked ? 'bg-amber-50 border-amber-200 text-amber-900' :
            value ? 'bg-green-50 border-green-500 border-2' : 'bg-white border-blue-400 border-2 hover:bg-blue-50'
          }`}
          data-testid={`field-${field.id}-wrap`}
        >
          {isPrefix && (
            <span className="flex items-center justify-center px-1.5 text-gray-600 select-none border-r border-gray-300 bg-gray-50/70">{symbol}</span>
          )}
          <input
            key={field.id}
            type="number"
            value={value || ''}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            placeholder={field.placeholder || field.label}
            disabled={isLocked}
            readOnly={isLocked}
            onFocus={() => setActiveFieldId(field.id)}
            onBlur={() => setActiveFieldId(null)}
            className="flex-1 min-w-0 px-1.5 outline-none bg-transparent"
            // <input> resets text-align in the UA stylesheet so the
            // parent fieldStyle alignment doesn't carry through —
            // mirror it explicitly so currency numbers honour the
            // recruiter's Left/Center/Right choice.
            style={{ textAlign: field.text_alignment || 'left' }}
            data-testid={`field-${field.id}`}
          />
          {!isPrefix && (
            <span className="flex items-center justify-center px-1.5 text-gray-600 select-none border-l border-gray-300 bg-gray-50/70">{symbol}</span>
          )}
        </div>
      );
    }

    return (
      <input
        key={field.id}
        type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : field.field_type === 'number' ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => updateFieldValue(field.id, e.target.value)}
        placeholder={field.placeholder || field.label}
        disabled={isLocked}
        readOnly={isLocked}
        onFocus={() => setActiveFieldId(field.id)}
        onBlur={() => setActiveFieldId(null)}
        style={fieldStyle}
        className={`${baseClasses} px-1.5 rounded border outline-none ${
          isLocked ? 'bg-amber-50 border-amber-200 text-amber-900 cursor-not-allowed' :
          value ? 'bg-green-50 border-green-500 border-2' : 'bg-white border-blue-400 border-2 focus:bg-white hover:bg-blue-50'
        }`}
        data-testid={`field-${field.id}`}
      />
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-xl shadow-lg p-8">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Unable to Load Document</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <p className="text-sm text-muted-foreground">Please contact the sender if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    const portalUrl = portalToken
      ? `/portal/access/${portalToken}`
      : `/portal?email=${encodeURIComponent(document?.candidate_email || '')}`;
    
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-xl shadow-lg p-8" data-testid="submission-success">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">Document Submitted!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you, {(() => {
              // Use latest field values for first/last name if candidate changed them
              // fieldValues is an object keyed by field_id
              let firstName, lastName;
              for (const [fid, val] of Object.entries(fieldValues || {})) {
                const f = fields.find(fl => fl.id === fid);
                const bh = f?.bullhorn_source_field || f?.mapped_bullhorn_field || '';
                if (bh === 'firstName') firstName = val;
                if (bh === 'lastName') lastName = val;
              }
              if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
              return document?.candidate_name;
            })()}. Your document has been submitted successfully.
          </p>
          <div className="bg-slate-50 rounded-lg p-6 text-left mb-6">
            <p className="text-sm text-muted-foreground mb-1">Document</p>
            <p className="font-medium mb-4">{template?.name}</p>
            <p className="text-sm text-muted-foreground mb-1">Submitted</p>
            <p className="font-medium">{format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
          </div>
          
          {/* Link to view other documents */}
          <a 
            href={portalUrl}
            className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
            data-testid="view-other-documents-link"
          >
            View All Your Documents
            <ChevronRight className="w-4 h-4" />
          </a>
          <p className="text-xs text-muted-foreground mt-3">
            Check if you have other documents to complete
          </p>
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200 flex flex-col" data-testid="candidate-completion-page">
      {/* Informational-only banner — recruiter has marked this doc as
          "for your records" and the candidate just needs to read &
          acknowledge.  Sits above the preview banner because info docs
          can't also be preview docs. */}
      {isInformational && !document?.preview_only && (
        <div
          className="bg-sky-100 border-b border-sky-300 text-sky-900 sticky top-0 z-[60]"
          data-testid="informational-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              For your records — please review &amp; acknowledge below
            </span>
            <span className="text-xs text-sky-700 hidden sm:inline">
              No fields required · Just scroll through and tap Acknowledge
            </span>
          </div>
        </div>
      )}

      {/* Preview-mode banner — only renders for recruiter-launched previews.
          Sticky above the header so it's always visible while scrolling.
          Submit is disabled in this mode (see handleSubmit) — the banner
          tells the recruiter why nothing happens when they click sign. */}
      {document?.preview_only && (
        <div
          className="bg-amber-100 border-b border-amber-300 text-amber-900 sticky top-0 z-[60]"
          data-testid="preview-mode-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 font-semibold">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
              Recruiter preview — this is what the candidate will see
            </span>
            <span className="text-xs text-amber-700">
              Submit disabled · No data will be saved · Close tab to return to editor
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-foreground">HyreDocs</p>
              <p className="text-xs text-muted-foreground">{template?.name}</p>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${canSubmit ? 'bg-green-500' : 'bg-orange-500'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm font-medium text-muted-foreground">{progress}%</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {completedFields.length} of {editableFields.length} fields
            </span>
          </div>
          
          <div className="text-right">
            <p className="text-sm font-medium">{document?.candidate_name}</p>
            <p className="text-xs text-muted-foreground">{document?.candidate_email}</p>
          </div>
        </div>
      </header>

      {/* Small banner to go back to all documents */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Completing: <strong>{template?.name}</strong>
          </p>
          <a 
            href={portalToken
              ? `/portal/access/${portalToken}`
              : `/portal?email=${encodeURIComponent(document?.candidate_email || '')}`}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            data-testid="back-to-portal-link"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to All Documents
          </a>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* PDF Viewer */}
        <main className="flex-1 p-2 sm:p-4 overflow-auto" ref={pdfContainerRef}>
          <div className="max-w-4xl mx-auto">
            {/* Per-template instructions from the recruiter — sanitized HTML */}
            {(document?.candidate_instructions || template?.candidate_instructions) && (
              <div
                className="mb-3 p-4 rounded-lg bg-amber-50 border border-amber-200"
                data-testid="candidate-instructions-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-amber-700" />
                  </div>
                  <div className="flex-1 prose prose-sm max-w-none text-amber-900">
                    <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 m-0 mb-1">
                      Instructions from {document?.company_name || 'your recruiter'}
                    </p>
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          document?.candidate_instructions || template?.candidate_instructions || '',
                          {
                            ALLOWED_TAGS: ['b','strong','i','em','u','a','p','br','ul','ol','li','span','div'],
                            ALLOWED_ATTR: ['href','target','rel','style'],
                          }
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {document?.template_mode === 'attachment_only' ? (
              <AttachmentOnlyPanel
                token={token}
                checklist={document?.attachment_checklist || []}
                signatureFieldId="attachment-only-acknowledgement"
                signatureValue={fieldValues['attachment-only-acknowledgement']}
                onOpenSignaturePad={() => {
                  setActiveFieldId('attachment-only-acknowledgement');
                  setSignatureFieldId('attachment-only-acknowledgement');
                }}
                onSubmit={handleSubmit}
                submitting={submitting}
                hasSignature={!!fieldValues['attachment-only-acknowledgement']}
              />
            ) : (
            <>
            {/* Toolbar */}
            <div className="bg-white rounded-t-xl border border-b-0 border-slate-300 px-2 sm:px-4 py-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                  {currentPage}/{totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Zoom controls - hidden on very small screens */}
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setScale(s => Math.max(0.4, s - 0.1))}
                  className="h-8 w-8 p-0"
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs sm:text-sm font-medium w-10 sm:w-14 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setScale(s => Math.min(2, s + 0.1))}
                  className="h-8 w-8 p-0"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>

              {/* Fields toggle for mobile */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFieldsList(!showFieldsList)}
                className="lg:hidden h-8 px-2 text-xs"
              >
                {completedRequiredFields.length}/{requiredFields.length}
              </Button>
            </div>

            {/* PDF Document with Fields */}
            <div className="bg-white border border-slate-300 shadow-lg overflow-x-auto">
              <div className="relative bg-gray-100 min-h-[400px] sm:min-h-[600px] flex items-start justify-center p-2 sm:p-4">
                {templatePdfUrl ? (
                  <Document
                    file={templatePdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-[400px] sm:h-[600px]">
                        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center h-[400px] sm:h-[600px] text-muted-foreground">
                        <div className="text-center">
                          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                          <p>Failed to load PDF</p>
                        </div>
                      </div>
                    }
                    className="relative"
                  >
                    <div className="relative shadow-xl">
                      <Page 
                        pageNumber={currentPage} 
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadSuccess={onPageLoadSuccess}
                      />
                      {/* Fields overlay on PDF */}
                      {pdfLoaded && currentPageFields.map(field => renderPdfField(field))}
                    </div>
                  </Document>
                ) : (
                  <div className="flex items-center justify-center h-[400px] sm:h-[600px] text-muted-foreground">
                    <div className="text-center">
                      <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="font-medium">No document available</p>
                      <p className="text-sm">Please contact the sender</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        </main>

        {/* Sidebar - Field list */}
        <aside className="w-72 bg-white border-l border-slate-200 flex flex-col hidden lg:flex">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-bold text-lg">Fields to Complete</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {completedRequiredFields.length} of {requiredFields.length} required done
            </p>
          </div>

          {/* Submit button + Next-required jump — sticky at top */}
          <div className="p-4 border-b border-slate-200 space-y-2 flex-shrink-0">
            {!isInformational && requiredFields.length > completedRequiredFields.length && (
              <Button
                type="button"
                variant="outline"
                onClick={() => jumpToOutstandingRequiredField('next')}
                className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="next-required-field-button"
              >
                Next required field
                <ChevronDown className="w-4 h-4 ml-2 -rotate-90" />
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className={`w-full ${canSubmit ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400'}`}
              data-testid="submit-document-button"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : isInformational ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Acknowledge &amp; Continue
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {canSubmit ? 'Submit Document' : `${requiredFields.length - completedRequiredFields.length} required left`}
                </>
              )}
            </Button>
          </div>
          
          <FieldStatusList
            fields={fields}
            fieldValues={fieldValues}
            activeFieldId={activeFieldId}
            isConditionMet={shouldRenderField}
            density="compact"
            onSelectField={(field) => {
              setCurrentPage(field.page || 1);
              setActiveFieldId(field.id);
            }}
          />
        </aside>
      </div>

      {/* Floating action bar — always visible at bottom of viewport */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3" data-testid="floating-action-bar">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            {/* Progress */}
            {!isInformational && (
              <div className="flex items-center gap-2 text-sm flex-shrink-0">
                <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{width: `${progress}%`}} />
                </div>
                <span className="text-muted-foreground whitespace-nowrap">{completedRequiredFields.length}/{requiredFields.length}</span>
              </div>
            )}
            {/* Save indicator */}
            {autoSaveStatus && (
              <span className={`text-xs flex-shrink-0 ${autoSaveStatus === 'saved' ? 'text-green-600' : autoSaveStatus === 'saving' ? 'text-amber-600' : 'text-red-500'}`} data-testid="autosave-status">
                {autoSaveStatus === 'saved' ? 'Saved' : autoSaveStatus === 'saving' ? 'Saving...' : 'Save failed'}
              </span>
            )}
            <div className="flex-1" />
            {/* Next required */}
            {!isInformational && requiredFields.length > completedRequiredFields.length && (
              <Button
                type="button"
                size="sm"
                onClick={() => jumpToOutstandingRequiredField('next')}
                className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white h-9 px-3 text-xs sm:text-sm"
                data-testid="floating-next-required"
              >
                Next required
                <ChevronDown className="w-3.5 h-3.5 ml-1 -rotate-90" />
              </Button>
            )}
            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              size="sm"
              className={`h-9 px-4 text-xs sm:text-sm ${canSubmit ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400'}`}
              data-testid="floating-submit-button"
            >
              {submitting ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Submitting</>
              ) : isInformational ? (
                <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Acknowledge</>
              ) : (
                <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />{canSubmit ? 'Submit' : `${requiredFields.length - completedRequiredFields.length} left`}</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Mobile bottom spacer — prevents floating bar from covering content */}
      {!submitted && <div className="h-16" />}

      {/* Mobile Fields List Modal */}
      {showFieldsList && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setShowFieldsList(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-lg">Fields to Complete</h2>
                <p className="text-sm text-muted-foreground">
                  {completedRequiredFields.length} of {requiredFields.length} required done
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowFieldsList(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <FieldStatusList
              fields={fields}
              fieldValues={fieldValues}
              activeFieldId={activeFieldId}
              isConditionMet={shouldRenderField}
              density="comfortable"
              onSelectField={(field) => {
                setCurrentPage(field.page || 1);
                setActiveFieldId(field.id);
                setShowFieldsList(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Signature modal */}
      {signatureFieldId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            {/* Mode tabs */}
            <div className="flex border rounded-lg p-1 bg-muted/30 mb-4">
              <button
                type="button"
                onClick={() => setSignatureMode('draw')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  signatureMode === 'draw' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                Draw
              </button>
              <button
                type="button"
                onClick={() => setSignatureMode('type')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  signatureMode === 'type' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                Type
              </button>
            </div>

            {signatureMode === 'draw' ? (
              <>
                <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white mb-4">
                  <canvas
                    ref={canvasRef}
                    width={450}
                    height={150}
                    className="w-full touch-none"
                    data-testid="signature-canvas"
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center mb-4">
                  Use your mouse or finger to draw your signature
                </p>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your full name"
                    className="w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg text-center text-2xl focus:outline-none focus:border-primary"
                    style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
                    data-testid="signature-type-input"
                    autoFocus
                  />
                </div>
                {typedSignature && (
                  <div className="border rounded-lg p-4 mb-4 bg-slate-50 flex items-center justify-center h-[100px]">
                    <span className="text-4xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}>
                      {typedSignature}
                    </span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground text-center mb-4">
                  Type your name to generate a signature
                </p>
              </>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setSignatureFieldId(null); setTypedSignature(''); setSignatureMode('draw'); }} className="flex-1">
                Cancel
              </Button>
              {signatureMode === 'draw' && (
                <Button variant="outline" onClick={clearSignature} className="flex-1">
                  <X className="w-4 h-4 mr-2" />Clear
                </Button>
              )}
              <Button onClick={saveSignature} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="accept-signature-btn">
                <Check className="w-4 h-4 mr-2" />Accept
              </Button>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
}
